


























import Vue from 'vue'
import VideoPreviewDisplay from '@/components/VideoPreviewDisplay'
import FontManager from '@/components/FontManager'
import { Cast } from '@/types/casts'
import { UserProfile } from '@/types/users'
import { Scene } from '@/types/scenes'
import { Stream, SourceStream, StreamDoc } from '@/types/streams'
import { Asset, VideoAsset, AssetType } from '@/types/assets'
import TimestampDisplay from '@/components/VideoPreviewDisplay/TimestampDisplay'
import { Team } from '@/types/teams'
import { CEF_DEBUG_MODE } from '.'
import './CefEvent.scss'
import env from '@/env'
import { KENV } from '@/types/admin'
import fb from '@/firebase'
import Watermark from '@/components/WaterMark/Watermark.vue'



// Set heartbeat interval duration
// note that each heartbeat update will trigger an update in fb snapshot subscribers
const HEART_BEAT_INTERVAL = 30 * 1000 // ms

export default Vue.extend({
  name: 'CefEvent',
  computed: {
    watermark (): boolean {
      return this.currentCast && this.currentCast.watermark
    },
    activeScene (): Scene | undefined {
      return this.$store.direct.getters.events.activeScene
    },
    teamStreams (): { [ streamId: string]: Stream } {
      return this.$store.direct.state.team.streams
    },
    streamDocs (): { [ streamId: string ]: StreamDoc } {
      return this.$store.direct.state.events.streamDocs
    },
    sceneAssets (): { [assetId: string]: Asset } {
      return this.$store.direct.state.assets.sceneAssets
    },
    currentCast (): Cast {
      return this.$store.direct.state.events.currentCast
    },
    simpleMode (): boolean {
      return this.$store.direct.state.events.simpleMode
    },
    castTeam (): UserProfile[] {
      return this.$store.direct.state.events.castTeam
    },
    currentUserId (): string {
      return this.$store.direct.state.user.id
    },
    userLoggedIn (): boolean {
      return this.$store.direct.state.user.userLoggedIn
    },
    sceneInPreview (): string | null {
      return this.$store.direct.state.events.sceneInPreview
    },
    cefEvent (): boolean {
      return this.$store.direct.state.events.cefEvent
    },
    iosFront (): boolean {
      return this.$store.direct.state.events.iosFront
    },
    cefMode (): string {
      return this.$store.direct.state.events.cefMode
    },
    cefMixer (): string {
      // Note: if cefMixer is missing all heartbeats will overwrite each other in the same global key (!)
      return this.$store.direct.state.events.cefMixer || 'global'
    },
    outputStreams (): { [id: string]: Stream } {
      return this.$store.direct.state.events.outputStreams
    },
    sourceStreams (): { [id: string]:SourceStream } {
      return this.$store.direct.state.events.sourceStreams
    },
    userProfile (): UserProfile | null {
      return this.$store.direct.state.user.profile
    },
    castTeamDoc (): Team {
      return this.$store.direct.state.events.castTeamDoc
    },
    outputBroadcastAudio (): boolean {
      return this.castTeamDoc.cef_nobroadcast_final !== undefined ? !this.castTeamDoc.cef_nobroadcast_final : true
    },
    timestampActive (): boolean {
      // console.log('timestampActive ', this.timestampStarted, this.currentCast, this.sourceStreams, this.activeScene)
      if (this.timestampStarted) return true
      return !!(this.currentCast !== null && this.sourceStreams && this.activeScene)
    }
  },
  watch: {
    timestampActive (newVal: boolean) {
      if (newVal) {
        this.timestampStarted = true
      }
    },
    userLoggedIn (newVal: boolean) {
      // this is if the validation is taking longer
      // we will fire off init (because you need to be logged in to get the data)
      if (newVal === true) {
        this.init()
      }
    },
    sourceStreams () {
      this.$store.direct.dispatch.events.updateSourceStreamDocs()
      this.setStreamNames()
    },
    castTeam: {
      handler () {
        this.setStreamNames()
      }
    },
    currentCast: {
      handler (newVal) {
        if (newVal.scenes !== undefined) {
          this.$store.direct.dispatch.assets.updateSceneAssets()
          this.gotScenes = true
        }
        if(newVal?.id !== undefined && newVal.id !== '' && this.dumpRequestSubscribed == false){
          try{
            const castId = this.$route.params.id
            this.$store.direct.dispatch.events.subscribeDumpStateRequests(castId)
            this.dumpRequestSubscribed = true
          } catch(error){
            console.log('subscribeDumpStateRequests failed', error)
          }
        }
      },
      deep: true
    }
  },
  created () {
    if (this.$route.name === 'cefEvent') {
      this.$store.direct.dispatch.events.setCefEvent()
      if (this.userLoggedIn === true) {
        this.init()
      }
      // register for changes to local storage
      if (this.cefMode === 'front') {
        window.addEventListener('storage', e => {
          if (e.key === 'currentCast') {
            this.$store.direct.dispatch.events.updateCurrentCastFromLocalStorage(e.newValue)
            // if we haven't loaded the cast items already
            if (!this.loadedCastItems) {
              this.loadCastItems()
            }
          }
        })
      }
    } else if (this.$route.name === 'iosFront') {
      this.initIos()
    }
  },
  destroyed () {
    clearTimeout(this.heartbeatTimer)
    this.$store.direct.dispatch.assets.cleanupSceneAssets()
    this.$store.direct.dispatch.assets.unsubscribeAssetGroups(this.currentCast.asset_groups)
    this.$store.direct.dispatch.events.unsubscribeCastTeam(undefined)
    this.$store.direct.dispatch.events.unsubscribeUpdateSourceStreamDocs()
    this.$store.direct.dispatch.events.unsubscribeDumpRequests(this.currentCast.id)
  },
  data: () => ({
    heartbeatTimer: null as any,
    streamsManager: {},
    lastFinished: 0,
    lastFinishedPl: 0,
    streamNames: {},
    streamImages: {},
    lastTimestampSent: [],
    debugMode: CEF_DEBUG_MODE && env.environment === KENV.Dev,
    gotScenes: false,
    loadedCastItems: false,
    timestampStarted: false,
    dumpRequestSubscribed: false,
  }),
  methods: {
    init (): void {
      this.$store.direct.dispatch.events.setInCast(true)
      if (this.cefMode === 'back') {
        this.initWithDatabase()
      } else if (this.cefMode === 'front') {
        this.initWithLocalStorage()
      }
    },
    async initIos (): Promise<void> {
      try {
        const token = this.$route.query.token
        await fb.firebase.auth().signInWithCustomToken(token)
        this.$store.direct.dispatch.events.setIosFront()
        this.$store.direct.dispatch.events.setInCast(true)
        this.initWithDatabase()
      } catch (error) {
        console.error(error)
      }

      try{
        const castId = this.currentCast.id
        this.$store.direct.dispatch.events.subscribeDumpStateRequests(castId)
      } catch(error){
        console.log('subscribeDumpStateRequests failed', error)
      }
    },
    initWithLocalStorage () {
      const currentCast = localStorage.getItem('currentCast')
      if (currentCast) {
        this.$store.direct.dispatch.events.updateCurrentCastFromLocalStorage(currentCast)
        this.loadCastItems()
      } else {
        setTimeout(() => {
          if (!this.loadedCastItems) this.initWithDatabase()
        }, 30000)
      }
    },
    initWithDatabase () {
      this.$store.direct.dispatch.events.subscribeCurrentCast(this.$route.params.id).then(() => {
        this.loadCastItems()
      }).catch(error => {
        console.error('Error init CefEvent', error)
      })
    },
    async loadCastItems () {
      // console.log('loadCastItems')

      this.$store.direct.dispatch.assets.subscribeAssetGroups(this.currentCast.asset_groups)
      this.$store.dispatch('events/getCastTeam', this.currentCast.team_id)
      this.loadedCastItems = true

      // trigger the first beat
      this.cefHeartbeat()
    },
    getUrlOfVideo (assetId: string): string {
      const asset = this.sceneAssets[assetId]
      if (asset === undefined || asset.type !== AssetType.Video) {
        return ''
      }
      const videoasset = asset as VideoAsset
      let url = videoasset.url
      if (url === undefined) {
        url = ''
      }
      return url
    },
    cefHeartbeat () {
      clearTimeout(this.heartbeatTimer)

      // see https://developer.mozilla.org/en-US/docs/Web/API/Performance/memory
      const castId = (this.currentCast) ? this.currentCast.id : 0
      if (!castId) {
        console.log('Cast id is missing - cannot start cef heartbeat')
        return
      }

      const performanceAPI = window.performance as any
      const cefData = {
        castId: castId,
        cefmode: this.cefMode, // front, back separate
        key: this.cefMixer,
        timestamp: fb.serverTimestamp(),
        memory: {
          limit: performanceAPI.memory.jsHeapSizeLimit,
          total: performanceAPI.memory.totalJSHeapSize,
          used: performanceAPI.memory.usedJSHeapSize
        }
        // ... add extra data if needed ...
      }

      // console.warn('heart beat', cefData)
      this.$store.dispatch('events/cefHeartbeat', cefData)

      // next heartbeat will only be triggered if JS didn't stop execution anywhere because of errors etc
      // console.log('setTimeout heartBeatTime')
      this.heartbeatTimer = setTimeout(this.cefHeartbeat, HEART_BEAT_INTERVAL)
    },
    setStreamNames (): void {
      const names = {}
      const images = {}
      for (const key in this.sourceStreams) {
        const stream = this.sourceStreams[key]
        if (stream.type === 'broadcast') {
          // get the name of the stream from where ever the team streams
          if (this.teamStreams.hasOwnProperty(key)) {
            names[key] = this.teamStreams[key].name
          } else {
            names[key] = 'Input Stream'
          }
        } else if (stream.type === 'caster') {
          const person = this.castTeam.find(member => { return member.user_id === stream.user_id })
          if (person) {
            if (person.display_name !== '') {
              names[key] = person.display_name
            } else {
              names[key] = `${person.first_name} ${person.last_name}`
            }
            if (person.display_image !== undefined && person.display_image !== '') {
              images[key] = person.display_image
            }
          }
        }
      }
      this.streamNames = names
      this.streamImages = images
    }
  },
  components: {
    VideoPreviewDisplay,
    FontManager,
    TimestampDisplay,
    Watermark
  }
})
